// =============================================================================
// InspectionItemScreen — /inspections/equipment/:kind/:itemId
//
// The one item page. Header card: identity, where it lives (kit → vehicle,
// or depot), who holds it, standing, last inspection; then the actions; then
// the inspection history with a PDF per record.
//
// Open to any field+ user — anyone holding the S/N can verify the item is
// in date. Field can't browse the open directory but they get here via S/N
// search or their gallery.
//
// 12 Oct 2026 — "Correct tag" on a rigging history row (manager+). James
// logged a quarterly on RG-0001 and the form defaulted to the quarter's
// colour (Yellow) when the tag on the gear was Blue. The brain's rule: a
// wrong colour is fixed ON THE INSPECTION RECORD with a note saying what was
// corrected and why, never on the item and never in the SQL editor. So this
// updates rigging_inspections.tag_colour / tag_number, appends a dated
// correction line to condition_notes, and — only when this is the item's
// latest inspection — mirrors the colour onto the item (the sync trigger
// fires on INSERT only). The audit trigger on rigging_inspections records
// the before/after. RLS: rigg_insp_write and rigg_update are manager+.
//
// 22 Oct 2026 (mig 444) — reads v_equipment_register (one vocabulary; ladders
// included, with ladder_inspections as their history). Actions on the one
// page: Inspect · Tag out (rigging) · Request (anyone, when they don't hold
// it) · Allocate / Approve (manager, director, supervisor — the one-tap RPCs)
// · "Fixed" on a defect row (manager or the inspector, note required). A
// pending item says who added it. Status words: Current / Due soon / Overdue
// / Never inspected / Out of service.
// =============================================================================

import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, HardHat, Wrench, AlignJustify, ShieldOff, Plus,
  FileDown, Pencil, Hand, UserCheck, CheckCircle2, Wrench as Fix,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Card, Pill, Spinner, EmptyState } from "@/components/ui";
import { downloadInspectionPdf } from "./inspectionPdf";
import { fetchInspectionPhotoDataUrl } from "./inspectionPhoto";
import {
  type RegisterRow, type Kind, KIND_LABEL, URGENCY_LABEL, URGENCY_TONE, URGENCY_TEXT,
  TagChip, standingLine, homeLine, inspectHref, fmtDay,
  useEquipmentKits, useOpenRequests, useEquipmentActions, AllocateDialog, ReasonSheet,
} from "@/features/equipment/equipmentShared";

interface RiggingInspection {
  inspection_id:    string;
  inspection_date:  string;
  inspection_type:  string | null;
  result:           string;
  tag_colour:       string | null;
  tag_number:       string | null;
  defects_found:    boolean;
  defect_severity:  string | null;
  defect_summary:   string | null;
  action_required:  string | null;
  findings:         string | null;
  condition_notes:  string | null;
  next_inspection_due: string | null;
  inspector_employee_id: string | null;
  photo_file_ids:   string[] | null;
  resolved:         boolean;
  resolved_date:    string | null;
  resolved_note:    string | null;
  inspector:        { first_name: string | null; last_name: string | null } | null;
}

interface ToolTest {
  tnt_id:                   string;
  tested_at:                string;
  result:                   string;
  tag_colour:               string | null;
  test_tag_number:          string | null;
  tester_licence_number:    string | null;
  visual_inspection_result: string | null;
  defects_found:            boolean;
  failure_reason:           string | null;
  action_required:          string | null;
  notes:                    string | null;
  next_test_due:            string | null;
  tester_employee_id:       string | null;
  resolved:                 boolean;
  resolved_date:            string | null;
  resolved_note:            string | null;
  tester:                   { first_name: string | null; last_name: string | null } | null;
}

interface LadderInspection {
  inspection_id:       string;
  inspection_date:     string;
  inspector_name:      string | null;
  result:              "pass" | "fail";
  checks:              Record<string, string> | null;
  defects_found:       string | null;
  action_taken:        string | null;
  next_inspection_due: string | null;
  inspector:           { first_name: string | null; last_name: string | null } | null;
}

const KIND_ICON: Record<Kind, typeof HardHat> = { rigging: HardHat, power_tool: Wrench, ladder: AlignJustify };

export default function InspectionItemScreen() {
  const { kind, itemId } = useParams<{ kind: Kind; itemId: string }>();
  const user             = useRequireAuth();
  const navigate         = useNavigate();
  const qc               = useQueryClient();
  const isRig            = kind === "rigging";
  const me               = user?.employee_id ?? null;
  const canAllocate      = hasRole(user, "supervisor");
  const isManager        = hasRole(user, "manager");

  const [tagOutOpen, setTagOutOpen] = useState(false);
  const [tagOutReason, setTagOutReason] = useState("");
  const [allocating, setAllocating] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const { data: item, isLoading: loadingItem } = useQuery({
    queryKey: ["inspection_item", kind, itemId],
    enabled: !!kind && !!itemId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("v_equipment_register")
        .select("*")
        .eq("kind", kind!)
        .eq("item_id", itemId!)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as RegisterRow | null;
    },
  });

  const { data: riggingHistory = [], isLoading: loadingRigging } = useQuery({
    queryKey: ["rigging_history", itemId],
    enabled: isRig && !!itemId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("rigging_inspections")
        .select(`
          inspection_id, inspection_date, inspection_type, result,
          tag_colour, tag_number, defects_found, defect_severity,
          defect_summary, action_required, findings, condition_notes,
          next_inspection_due, inspector_employee_id, photo_file_ids,
          resolved, resolved_date, resolved_note,
          inspector:inspector_employee_id(first_name, last_name)
        `)
        .eq("rigging_id", itemId!)
        .order("inspection_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as RiggingInspection[];
    },
  });

  const { data: toolHistory = [], isLoading: loadingTool } = useQuery({
    queryKey: ["tool_history", itemId],
    enabled: kind === "power_tool" && !!itemId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("test_and_tag")
        .select(`
          tnt_id, tested_at, result, tag_colour, test_tag_number, tester_licence_number,
          visual_inspection_result, defects_found, failure_reason,
          action_required, notes, next_test_due, tester_employee_id,
          resolved, resolved_date, resolved_note,
          tester:tester_employee_id(first_name, last_name)
        `)
        .eq("power_tool_id", itemId!)
        .order("tested_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ToolTest[];
    },
  });

  const { data: ladderHistory = [], isLoading: loadingLadder } = useQuery({
    queryKey: ["ladder_history", itemId],
    enabled: kind === "ladder" && !!itemId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("ladder_inspections")
        .select(`
          inspection_id, inspection_date, inspector_name, result, checks, defects_found, action_taken, next_inspection_due,
          inspector:inspector_employee_id(first_name, last_name)
        `)
        .eq("asset_id", itemId!)
        .order("inspection_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as LadderInspection[];
    },
  });

  const { data: kits = [] } = useEquipmentKits(canAllocate);
  const { data: requests = [] } = useOpenRequests(!!user);
  const { data: employees = [] } = useQuery({
    queryKey: ["equipment_employees"],
    enabled: canAllocate,
    queryFn: async () => {
      const { data, error } = await supabase.from("employees").select("employee_id, first_name, last_name").eq("is_active", true).order("first_name");
      if (error) throw error;
      return (data ?? []) as { employee_id: string; first_name: string | null; last_name: string | null }[];
    },
  });
  const { request, approve, resolveDefect } = useEquipmentActions();
  const openHere = useMemo(() => requests.filter(q => q.kind === kind && q.item_id === itemId), [requests, kind, itemId]);
  const myOpenRequest = openHere.some(q => q.requested_by_employee_id === me);

  const tagOut = useMutation({
    mutationFn: async () => {
      if (!isRig) throw new Error("Tag-out is for rigging. For a power tool or ladder, log a Fail inspection — it comes out of service.");
      if (!tagOutReason.trim()) throw new Error("Add a reason so the audit trail is clean.");
      const { error } = await supabase.schema("app").rpc("tag_out_rigging", { p_rigging_id: itemId!, p_reason: tagOutReason.trim() });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inspection_item", kind, itemId] });
      qc.invalidateQueries({ queryKey: ["rigging_history", itemId] });
      qc.invalidateQueries({ queryKey: ["equipment_register"] });
      setTagOutOpen(false); setTagOutReason("");
    },
    onError: (e: any) => window.alert(e?.message ?? "Tag out failed"),
  });

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ["inspection_item", kind, itemId] });
    qc.invalidateQueries({ queryKey: ["rigging_history", itemId] });
    qc.invalidateQueries({ queryKey: ["tool_history", itemId] });
    qc.invalidateQueries({ queryKey: ["ladder_history", itemId] });
    qc.invalidateQueries({ queryKey: ["equipment_register"] });
    qc.invalidateQueries({ queryKey: ["inspection_register"] });
  };

  if (loadingItem) return <div className="min-h-[40vh] flex items-center justify-center"><Spinner/></div>;
  if (!item || !kind) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><div className="p-6 text-text-muted text-sm">Item not found — it may be retired, or the link is old.</div></Card>
      </div>
    );
  }

  const Icon = KIND_ICON[kind];
  const mine = !!me && item.holder_employee_id === me;
  const out  = item.urgency === "out_of_service";
  // the shape the PDFs and the export were written against
  const legacyItem = {
    code: item.code, asset_tag: item.asset_tag ?? item.code, display_name: item.display_name, item_class: item.item_class, item_type: item.item_type,
    make: item.make, model: item.model, serial_number: item.serial_number, swl_kg: item.swl_kg, kit_name: item.kit_name, assigned_to_name: item.holder_name,
  };
  const historyCount = isRig ? riggingHistory.length : kind === "power_tool" ? toolHistory.length : ladderHistory.length;

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto pb-24">
      <button type="button" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/equipment"))} className="text-text-muted text-sm flex items-center gap-1 hover:text-text mb-2">
        <ArrowLeft size={14}/> Back
      </button>

      <Card className={`mb-4 ${out ? "border-danger/50" : ""}`}>
        <div className="p-4">
          <div className="flex items-start gap-3 mb-3">
            {kind === "ladder"
              ? <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${out ? "text-danger bg-danger/10 border border-dashed border-danger/50" : "bg-ink-raised text-text-muted"}`}><Icon size={20}/></div>
              : <TagChip colour={item.tag_colour} number={item.tag_number} out={out}/>}
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-semibold tracking-tight leading-tight">{item.display_name ?? item.code}</h1>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <span className="font-mono text-[12px] text-text-muted">{item.code}</span>
                {item.asset_tag && item.asset_tag !== item.code && <span className="font-mono text-[12px] text-text-muted">· Tag {item.asset_tag}</span>}
                <Pill label={KIND_LABEL[kind]} small/>
                <Pill label={URGENCY_LABEL[item.urgency]} small tone={URGENCY_TONE[item.urgency]}/>
                {item.registration_status === "pending" && <Pill label="pending approval" small tone="notice"/>}
                {item.open_defects > 0 && <Pill label={`${item.open_defects} defect${item.open_defects === 1 ? "" : "s"} open`} small tone="critical"/>}
              </div>
              <div className={`text-[12px] font-semibold mt-1.5 ${URGENCY_TEXT[item.urgency]}`}>{standingLine(item)}</div>
              <div className="text-[12px] text-text-muted mt-0.5">
                {item.holder_name ? <>Held by <span className="text-text">{item.holder_name}{mine ? " (you)" : ""}</span> · </> : <>Nobody holds it · </>}
                <span className="text-text">{homeLine(item)}</span>
                {item.registration_status === "pending" && item.submitted_by_name && <> · added by {item.submitted_by_name}</>}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-[12px] border-t border-line pt-3">
            {(item.make || item.model) && <KV label="Make / model" value={[item.make, item.model].filter(Boolean).join(" ")}/>}
            {item.serial_number && <KV label="Serial #" value={item.serial_number} mono/>}
            {item.item_type  && <KV label="Type"  value={item.item_type}/>}
            {item.item_class && <KV label="Class" value={item.item_class}/>}
            {item.swl_kg != null && <KV label="SWL" value={`${item.swl_kg} kg`}/>}
            <KV label="Last inspected" value={item.last_inspection_date ? fmtDay(item.last_inspection_date, true) : "—"}/>
            <KV label="Result" value={item.last_result ?? "—"}/>
            <KV label="Next due" value={item.next_due ? fmtDay(item.next_due, true) : "—"}/>
            <KV label="Inspector" value={item.last_inspector_name ?? "—"}/>
          </div>

          {/* actions */}
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <Link to={inspectHref(item)}><Button variant="primary" icon={Plus} size="sm">{kind === "power_tool" ? "Test & tag" : "Inspect"}</Button></Link>
            {isRig && item.status !== "quarantined" && (
              <Button variant="danger" icon={ShieldOff} size="sm" onClick={() => setTagOutOpen(true)}>Tag out</Button>
            )}
            {!mine && !canAllocate && (
              myOpenRequest
                ? <span className="text-[12px] text-accent border border-accent/40 rounded px-2.5 py-1.5">Requested — waiting on a manager</span>
                : <Button variant="secondary" icon={Hand} size="sm" onClick={() => setRequesting(true)}>Request it</Button>
            )}
            {canAllocate && (
              <Button variant="secondary" icon={UserCheck} size="sm" onClick={() => setAllocating(true)}>Allocate</Button>
            )}
            {canAllocate && item.registration_status === "pending" && (
              <Button variant="secondary" icon={CheckCircle2} size="sm" disabled={approve.isPending}
                onClick={() => approve.mutate({ kind, item_id: item.item_id }, { onSuccess: refreshAll, onError: (e: any) => window.alert(e?.message ?? "Could not approve") })}>
                {approve.isPending ? "Approving…" : "Approve onto the register"}
              </Button>
            )}
          </div>

          {canAllocate && openHere.length > 0 && (
            <div className="mt-3 p-3 rounded border border-accent/30 bg-accent/5 text-[12px]">
              <div className="font-medium mb-1 flex items-center gap-1.5"><Hand size={12} className="text-accent"/> {openHere.length === 1 ? "A request is open on this item" : `${openHere.length} requests are open on this item`}</div>
              {openHere.map(q => (
                <div key={q.request_id} className="text-text-muted">
                  {[q.requested_by?.first_name, q.requested_by?.last_name].filter(Boolean).join(" ") || "Someone"} · {fmtDay(q.created_at, true)}{q.note ? ` · ${q.note}` : ""}
                </div>
              ))}
              <div className="text-text-dim mt-1">Allocate closes them; Decline lives on Equipment → Needs a look.</div>
            </div>
          )}
        </div>
      </Card>

      {tagOutOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setTagOutOpen(false)}>
          <div className="bg-ink-card border border-line rounded-lg w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3"><ShieldOff size={16} className="text-danger"/><div className="text-[15px] font-semibold">Tag out item</div></div>
            <p className="text-[12px] text-text-muted mb-3">Withdraws this item from service. Logged in the audit trail. It stays out of service until an inspector logs a Pass.</p>
            <textarea value={tagOutReason} onChange={e => setTagOutReason(e.target.value)} placeholder="Reason — what's wrong with it?" rows={3}
              className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text mb-3"/>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setTagOutOpen(false)}>Cancel</Button>
              <Button variant="danger" icon={ShieldOff} disabled={tagOut.isPending} onClick={() => tagOut.mutate()}>{tagOut.isPending ? "Tagging out…" : "Tag out"}</Button>
            </div>
          </div>
        </div>
      )}
      {allocating && (
        <AllocateDialog row={item} kits={kits} employees={employees} onClose={() => setAllocating(false)}
          onDone={() => { if (item.registration_status === "pending") approve.mutate({ kind, item_id: item.item_id }); setAllocating(false); refreshAll(); }}/>
      )}
      {requesting && (
        <ReasonSheet title={`Ask for ${item.code}`} blurb={`${item.display_name ?? ""} — ${item.holder_name ? `held by ${item.holder_name}` : homeLine(item)}. Every allocator gets a notification; one tap on their side allocates it to you.`}
          placeholder="What's it for? (optional)" cta="Send request" busy={request.isPending} onClose={() => setRequesting(false)}
          onSubmit={(text) => request.mutate({ kind, item_id: item.item_id, note: text || undefined }, { onSuccess: () => { setRequesting(false); refreshAll(); }, onError: (e: any) => window.alert(e?.message ?? "Could not send the request") })}/>
      )}

      {/* History */}
      <div className="mb-2">
        <h2 className="text-[11px] font-semibold text-text-muted uppercase tracking-wider2 mb-2 px-1">
          {kind === "power_tool" ? "Test & tag history" : "Inspection history"} ({historyCount})
        </h2>
        {(loadingRigging || loadingTool || loadingLadder) ? (
          <div className="py-6 flex justify-center"><Spinner/></div>
        ) : historyCount === 0 ? (
          <Card><div className="p-4"><EmptyState title="Nothing on record yet" description="Log the first one to start the trail."/></div></Card>
        ) : isRig ? (
          <div className="space-y-2">
            {riggingHistory.map(h => (
              <RiggingHistoryRow key={h.inspection_id} item={legacyItem} h={h}
                canCorrect={isManager}
                canResolve={isManager || (!!me && h.inspector_employee_id === me)}
                isLatest={h.inspection_id === riggingHistory[0]?.inspection_id}
                correctorName={[user?.first_name, user?.last_name].filter(Boolean).join(" ") || "manager"}
                onResolve={(note) => resolveDefect.mutate({ kind: "rigging", inspection_id: h.inspection_id, note }, { onSuccess: refreshAll, onError: (e: any) => window.alert(e?.message ?? "Could not resolve") })}
                resolving={resolveDefect.isPending}
                onCorrected={refreshAll}/>
            ))}
          </div>
        ) : kind === "power_tool" ? (
          <div className="space-y-2">
            {toolHistory.map(t => (
              <ToolHistoryRow key={t.tnt_id} item={legacyItem} t={t}
                canResolve={isManager || (!!me && t.tester_employee_id === me)}
                onResolve={(note) => resolveDefect.mutate({ kind: "power_tool", inspection_id: t.tnt_id, note }, { onSuccess: refreshAll, onError: (e: any) => window.alert(e?.message ?? "Could not resolve") })}
                resolving={resolveDefect.isPending}/>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {ladderHistory.map(l => <LadderHistoryRow key={l.inspection_id} l={l}/>)}
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

function KV({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted">{label}</div>
      <div className={`text-[12px] mt-0.5 text-text ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

const TAG_COLOURS = ["Red", "Green", "Blue", "Yellow"] as const;

function localDayKey(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function DefectBlock({ severity, summary, action, resolved, resolvedDate, resolvedNote, canResolve, resolving, onResolve }: {
  severity?: string | null; summary?: string | null; action?: string | null;
  resolved: boolean; resolvedDate: string | null; resolvedNote: string | null;
  canResolve: boolean; resolving: boolean; onResolve: (note: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`mt-2 px-3 py-2 rounded text-[12px] border ${resolved ? "bg-ok/8 border-ok/30" : "bg-danger/8 border-danger/30"}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className={`font-medium mb-0.5 ${resolved ? "text-ok" : "text-danger"}`}>{severity ?? "Defect"}{resolved ? ` · fixed ${resolvedDate ? fmtDay(resolvedDate, true) : ""}` : " · open"}</div>
          {summary && <div className="text-text">{summary}</div>}
          {action && <div className="text-text-muted mt-1">Action: {action}</div>}
          {resolved && resolvedNote && <div className="text-text-muted mt-1">Fixed: {resolvedNote}</div>}
        </div>
        {!resolved && canResolve && !open && (
          <Button size="sm" variant="secondary" icon={Fix} onClick={() => setOpen(true)}>Fixed</Button>
        )}
      </div>
      {open && (
        <ReasonSheet title="Mark this defect fixed" blurb="Say what was done — it goes on the record. The item still comes back into service only on a Pass inspection."
          placeholder="e.g. Replaced the sling, re-tagged" cta="Mark fixed" required busy={resolving}
          onClose={() => setOpen(false)} onSubmit={(t) => { onResolve(t); setOpen(false); }}/>
      )}
    </div>
  );
}

function RiggingHistoryRow({ item, h, canCorrect, canResolve, isLatest, correctorName, onCorrected, onResolve, resolving }: {
  item: { code: string; asset_tag: string; display_name: string | null; item_class: string | null; item_type: string | null; make: string | null; model: string | null; serial_number: string | null; swl_kg: number | null; kit_name: string | null; assigned_to_name: string | null };
  h: RiggingInspection; canCorrect: boolean; canResolve: boolean; isLatest: boolean; correctorName: string; onCorrected: () => void;
  onResolve: (note: string) => void; resolving: boolean;
}) {
  const inspectorName = [h.inspector?.first_name, h.inspector?.last_name].filter(Boolean).join(" ") || "Unknown";
  const isFail = h.result === "Fail" || h.result === "Quarantine" || h.result === "Destroy";
  const [open, setOpen]       = useState(false);
  const [colour, setColour]   = useState<string>(h.tag_colour ?? "");
  const [tagNo, setTagNo]     = useState<string>(h.tag_number ?? "");
  const [reason, setReason]   = useState("");
  const [err, setErr]         = useState<string | null>(null);

  const correct = useMutation({
    mutationFn: async () => {
      if (!colour) throw new Error("Pick the colour that is actually on the tag.");
      if (!reason.trim()) throw new Error("Say why — the correction note goes on the record.");
      const changes: string[] = [];
      if (colour !== (h.tag_colour ?? ""))        changes.push(`colour ${h.tag_colour ?? "none"} → ${colour}`);
      if (tagNo.trim() !== (h.tag_number ?? ""))  changes.push(`tag number ${h.tag_number ?? "none"} → ${tagNo.trim() || "none"}`);
      if (changes.length === 0) throw new Error("Nothing changed.");
      const note = `[Corrected ${localDayKey()} by ${correctorName}: ${changes.join(", ")} — ${reason.trim()}]`;
      const { error } = await supabase
        .from("rigging_inspections")
        .update({ tag_colour: colour, tag_number: tagNo.trim() || null, condition_notes: [h.condition_notes, note].filter(Boolean).join("\n") })
        .eq("inspection_id", h.inspection_id);
      if (error) throw error;
      if (isLatest) {
        const { error: e2 } = await supabase.from("rigging_equipment").update({ tag_colour: colour, tag_number: tagNo.trim() || null }).eq("rigging_id", (h as any).rigging_id ?? undefined);
        if (e2 && !String(e2.message).includes("rigging_id")) throw e2;
      }
    },
    onSuccess: () => { setOpen(false); setReason(""); setErr(null); onCorrected(); },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Correction failed"),
  });

  return (
    <Card className={isFail ? "border-danger/30" : ""}>
      <div className="p-3">
        {open && (
          <div className="mb-3 p-3 rounded border border-accent/30 bg-accent/5">
            <div className="text-[12px] font-medium mb-2 flex items-center gap-1.5"><Pencil size={12} className="text-accent"/> Correct the tag on this record</div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <label className="text-[11px] text-text-muted">Colour on the gear
                <select value={colour} onChange={e => setColour(e.target.value)} className="mt-1 w-full bg-ink-raised border border-line rounded px-2 py-1.5 text-[12px] text-text outline-none">
                  <option value="">— pick —</option>
                  {TAG_COLOURS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="text-[11px] text-text-muted">Tag number
                <input value={tagNo} onChange={e => setTagNo(e.target.value)} className="mt-1 w-full bg-ink-raised border border-line rounded px-2 py-1.5 text-[12px] text-text outline-none font-mono" placeholder="optional"/>
              </label>
            </div>
            <label className="text-[11px] text-text-muted block mb-2">Why
              <input value={reason} onChange={e => setReason(e.target.value)} className="mt-1 w-full bg-ink-raised border border-line rounded px-2 py-1.5 text-[12px] text-text outline-none" placeholder="e.g. form defaulted to the quarter colour; the tag fitted is Blue"/>
            </label>
            {err && <div className="mb-2 text-[12px] text-danger">{err}</div>}
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => { setOpen(false); setErr(null); }}>Cancel</Button>
              <Button size="sm" variant="primary" onClick={() => correct.mutate()} disabled={correct.isPending}>{correct.isPending ? "Saving…" : "Save correction"}</Button>
            </div>
          </div>
        )}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[13px] font-medium">{fmtDay(h.inspection_date, true)}</span>
              <Pill label={h.result} small tone={isFail ? "critical" : h.result === "Pass" ? "approved" : "warning"}/>
              {h.tag_colour && <Pill label={`${h.tag_colour}${h.tag_number ? ` · ${h.tag_number}` : ""}`} small/>}
              {h.inspection_type && <span className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted">{h.inspection_type}</span>}
            </div>
            <div className="text-[11px] text-text-muted mt-1">By {inspectorName}{h.next_inspection_due && <> · Next due {fmtDay(h.next_inspection_due, true)}</>}</div>
            {h.condition_notes && <p className="text-[12px] text-text-muted mt-2 whitespace-pre-line">{h.condition_notes}</p>}
            {h.defects_found && (
              <DefectBlock severity={h.defect_severity} summary={h.defect_summary} action={h.action_required}
                resolved={h.resolved} resolvedDate={h.resolved_date} resolvedNote={h.resolved_note}
                canResolve={canResolve} resolving={resolving} onResolve={onResolve}/>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button size="sm" variant="ghost" icon={FileDown}
              onClick={async () => {
                let photoDataUrl: string | undefined;
                const firstPhotoId = h.photo_file_ids?.[0];
                if (firstPhotoId) { const url = await fetchInspectionPhotoDataUrl(firstPhotoId); photoDataUrl = url ?? undefined; }
                await downloadInspectionPdf({ kind: "rigging", item, inspection: h, inspectorName, photoDataUrl });
              }}>
              PDF
            </Button>
            {canCorrect && !open && (
              <Button size="sm" variant="ghost" icon={Pencil} onClick={() => setOpen(true)} title="Correct the tag colour or number on this record">Correct tag</Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function ToolHistoryRow({ item, t, canResolve, onResolve, resolving }: {
  item: { code: string; asset_tag: string; display_name: string | null; item_class: string | null; make: string | null; model: string | null; serial_number: string | null; kit_name: string | null; assigned_to_name: string | null };
  t: ToolTest; canResolve: boolean; onResolve: (note: string) => void; resolving: boolean;
}) {
  const testerName = [t.tester?.first_name, t.tester?.last_name].filter(Boolean).join(" ") || "Unknown";
  const isFail = t.result?.toLowerCase() === "fail";
  return (
    <Card className={isFail ? "border-danger/30" : ""}>
      <div className="p-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[13px] font-medium">{fmtDay((t.tested_at ?? "").slice(0, 10), true)}</span>
              <Pill label={t.result?.toUpperCase() ?? "—"} small tone={isFail ? "critical" : "approved"}/>
              {t.tag_colour && <Pill label={`${t.tag_colour}${t.test_tag_number ? ` · ${t.test_tag_number}` : ""}`} small/>}
            </div>
            <div className="text-[11px] text-text-muted mt-1">By {testerName}{t.tester_licence_number && <> · licence {t.tester_licence_number}</>}{t.next_test_due && <> · Next due {fmtDay(t.next_test_due, true)}</>}</div>
            {t.notes && <p className="text-[12px] text-text-muted mt-2 whitespace-pre-line">{t.notes}</p>}
            {(t.defects_found || isFail) && (
              <DefectBlock severity={isFail ? "Failed test" : "Defect"} summary={t.failure_reason} action={t.action_required}
                resolved={t.resolved} resolvedDate={t.resolved_date} resolvedNote={t.resolved_note}
                canResolve={canResolve && t.defects_found} resolving={resolving} onResolve={onResolve}/>
            )}
          </div>
          <Button size="sm" variant="ghost" icon={FileDown} onClick={() => downloadInspectionPdf({ kind: "power_tool", item, test: t, inspectorName: testerName })}>PDF</Button>
        </div>
      </div>
    </Card>
  );
}

function LadderHistoryRow({ l }: { l: LadderInspection }) {
  const who = [l.inspector?.first_name, l.inspector?.last_name].filter(Boolean).join(" ") || l.inspector_name || "Unknown";
  const isFail = l.result === "fail";
  const bad = Object.entries(l.checks ?? {}).filter(([, v]) => v === "defect").map(([k]) => k);
  return (
    <Card className={isFail ? "border-danger/30" : ""}>
      <div className="p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] font-medium">{fmtDay(l.inspection_date, true)}</span>
          <Pill label={isFail ? "FAIL" : "PASS"} small tone={isFail ? "critical" : "approved"}/>
        </div>
        <div className="text-[11px] text-text-muted mt-1">By {who}{l.next_inspection_due && <> · Next due {fmtDay(l.next_inspection_due, true)}</>}</div>
        {bad.length > 0 && <div className="text-[11px] text-text-muted mt-1">Checks failed: {bad.join(", ")}</div>}
        {(l.defects_found || l.action_taken) && (
          <div className="mt-2 px-3 py-2 rounded text-[12px] border bg-danger/8 border-danger/30">
            {l.defects_found && <div className="text-text">{l.defects_found}</div>}
            {l.action_taken && <div className="text-text-muted mt-1">Action: {l.action_taken}</div>}
          </div>
        )}
      </div>
    </Card>
  );
}
